<template>
  <div>
    <div class="px-4 py-6 lg:px-6 box-container flex flex-col items-center">
      <svg role="presentation" focusable="false" tabindex="-1" class="text-amber-500 icon icon-lg mx-3"
         
        alt=""
        svg-inline
       fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
  <path fill="currentColor" d="M16 32a16 16 0 1 0 0-32 16 16 0 0 0 0 32Z"/>
  <path fill="#fff" fill-rule="evenodd" clip-rule="evenodd" d="M16 4.3a2.9 2.9 0 0 1 2.9 3.1l-1.2 10.3c0 .9-.8 1.5-1.6 1.5-1 0-1.6-.6-1.7-1.5L13.2 7.4A2.8 2.8 0 0 1 16 4.3Z"/>
  <path fill="#fff" d="M16 26.7a2.1 2.1 0 1 0 0-4.3 2.1 2.1 0 0 0 0 4.3Z"/>
</svg>
      <div class="mt-2 font-semibold text-20 lg:text-24">
        {{ $t('no_results') }}
      </div>
      <p class="mt-2 lg:mb-6 text-16 lg:text-18 text-center" v-html="copies.subtitle" />
      <div v-if="state !== 'NO_RESULTS'" class="w-full md:max-w-sm mb-3">
        <p
          v-if="state === 'HIGHER_AMOUNT'"
          class="mt-4 text-20 lg:text-24 text-center font-semibold"
        >
          {{ $t('imt_no_results.try_higher_amount') }}
        </p>
        <p
          class="mt-4 px-3 py-4 text-16 lg:text-18 text-white bg-brand-800 rounded-6 relative arrow"
          v-html="copies.try"
        />
        <form @submit.prevent="onSubmit" class="mt-4 p-4 bg-gray-100 rounded-6">
          <p class="text-12 lg:text-14 text-gray-600 font-bold" v-html="copies.send" />
          <div v-if="state === 'TO_DEFAULT' || state === 'TO_USD'" class="mt-1 text-20 lg:text-24">
            <span class="font-semibold">{{ suggestedCurrency?.iso }}</span>
            ({{ suggestedCurrency?.labels.plural }})
          </div>
          <template v-else>
            <div
              class="border-3 border-solid border-brand-600 flex items-center justify-between bg-white rounded-6 p-3 mt-1"
            >
              <input
                type="number"
                inputmode="numeric"
                required
                class="text-18 font-bold outline-none min-w-0"
                v-model="newAmount"
                @invalid="onInvalid"
                @input="onInput"
              />
              <span class="text-18 text-gray-500 font-bold">{{
                state === 'HIGHER_AMOUNT' ? corridor.currencyFrom.iso : suggestedCurrency?.iso
              }}</span>
            </div>
            <p class="mt-2 text-12 lg:text-14">
              <span class="capitalize">{{ corridor.countryFrom.labels.from }}</span>
              {{ corridor.countryTo.labels.to }} ({{ suggestedCurrency?.iso }})
            </p>
          </template>
          <button type="submit" class="mt-4 btn btn-sm btn-amber w-full">
            {{ $t('imt_no_results.find_providers') }}
            <svg role="presentation" focusable="false" tabindex="-1" class="icon icon-md mx-2"   alt="" svg-inline  xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
  <path fill="currentColor" d="M505 442.7L405.3 343c-4.5-4.5-10.6-7-17-7H372c27.6-35.3 44-79.7 44-128C416 93.1 322.9 0 208 0S0 93.1 0 208s93.1 208 208 208c48.3 0 92.7-16.4 128-44v16.3c0 6.4 2.5 12.5 7 17l99.7 99.7c9.4 9.4 24.6 9.4 33.9 0l28.3-28.3c9.4-9.4 9.4-24.6.1-34zM208 336c-70.7 0-128-57.2-128-128 0-70.7 57.2-128 128-128 70.7 0 128 57.2 128 128 0 70.7-57.2 128-128 128z"/>
</svg>
          </button>
        </form>
      </div>
      <a class="mt-3 link font-normal dark" href="#imt-search-modal">
        {{ copies.newSearch }}
      </a>
    </div>
    <CrossSellTiles />
    <NewProviderAlertCard
      v-if="!IS_MOBILE_APP"
      class="mt-4 lg:mt-6"
      case="NO_RESULTS"
      :comparison-id="comparisonId"
    />
  </div>
</template>

<script lang="ts" setup>
import { noResultsWarning } from '../analytics/actions'
import NewProviderAlertCard from '../new-provider-alert-card.vue'
import CrossSellTiles from '../cross-sell-tiles/cross-sell-tiles.vue'
import type { Corridor } from '@core/types/imt'

const IS_MOBILE_APP = __IS_MOBILE_APP__

const { subscriptions } = useRuntimeConfig().public

const corridor = inject('corridor') as Corridor
const { $track, $t, $masterData, $lang } = useNuxtApp()

const props = defineProps<{
  amountUsd: number
  comparisonId: string
  landingMode: boolean
}>()

const route = useRoute()

const LOW_AMOUNT_THRESHOLD = 50

type State = 'HIGHER_AMOUNT' | 'TO_DEFAULT' | 'TO_USD' | 'FROM_USD' | 'NO_RESULTS'

const state = computed<State>(() => {
  if (route.query.noResultsRedirect) {
    return 'NO_RESULTS'
  }
  if (props.amountUsd < LOW_AMOUNT_THRESHOLD) {
    return 'HIGHER_AMOUNT'
  }
  if (corridor.currencyTo.iso !== corridor.countryTo.defaultCurrency.iso) {
    return 'TO_DEFAULT'
  }
  if (corridor.currencyTo.iso !== 'USD') {
    return 'TO_USD'
  }
  if (corridor.currencyFrom.iso !== 'USD') {
    return 'FROM_USD'
  }
  return 'NO_RESULTS'
})

const usdRate = computed(() => {
  return props.amountUsd / (corridor.amount || corridor.defaultAmount)
})

// 200$ equivalent rounded to the nearest 100
const suggestedAmount = computed(() => {
  if (state.value === 'FROM_USD') {
    return Math.round((usdRate.value * (corridor.amount || corridor.defaultAmount)) / 100) * 100
  }
  return Math.round(((1 / usdRate.value) * 200) / 100) * 100
})
const newAmount = ref(suggestedAmount.value)

const suggestedCurrency = computed(() => {
  if (state.value === 'TO_DEFAULT') {
    return $masterData.currencies.get(corridor.countryTo.defaultCurrency.iso)
  }
  if (state.value === 'TO_USD' || state.value === 'FROM_USD') {
    return $masterData.currencies.get('USD')
  }
  return corridor.currencyTo
})

const copies = computed(() => {
  switch (state.value) {
    case 'HIGHER_AMOUNT':
      return {
        subtitle: $t('imt_no_results.higher_amount_subtitle', {
          formattedAmount: formatMoney(
            corridor.amount || corridor.defaultAmount,
            corridor.currencyFrom.iso,
            $lang,
            0,
          ),
          fromCountry: corridor.countryFrom.labels.from,
          toCountry: corridor.countryTo.labels.to,
        }),
        try: $t('imt_no_results.try_at_least', {
          formattedAmount: formatMoney(suggestedAmount.value, corridor.currencyFrom.iso, $lang),
        }),
        send: $t('send'),
        newSearch: $t('imt_no_results.or_new_search'),
      }
    case 'TO_DEFAULT':
    case 'TO_USD':
      return {
        subtitle: $t('imt_no_results.alt_receiving_curr_subtitle', {
          currencyToLabel: corridor.currencyTo.labels.plural,
          currencyToIso: corridor.currencyTo.iso,
          fromCountry: corridor.countryFrom.labels.from,
          toCountry: corridor.countryTo.labels.to,
        }),
        try: $t('imt_no_results.try_receiving_curr', {
          suggestedCurrLabel: suggestedCurrency.value?.labels.plural,
        }),
        send: $t('imt_no_results.send_to', {
          formattedAmount: formatMoney(
            corridor.amount || corridor.defaultAmount,
            corridor.currencyFrom.iso,
            $lang,
          ),
          toCountry: corridor.countryTo.labels.to,
        }),
        newSearch: $t('imt_no_results.or_new_search'),
      }
    case 'FROM_USD':
      return {
        subtitle: $t('imt_no_results.alt_sending_curr_subtitle', {
          currencyFromLabel: corridor.currencyFrom.labels.plural,
          currencyFromIso: corridor.currencyFrom.iso,
          fromCountry: corridor.countryFrom.labels.from,
          toCountry: corridor.countryTo.labels.to,
        }),
        try: $t('imt_no_results.try_sending_curr', {
          suggestedCurrLabel: suggestedCurrency.value?.labels.plural,
        }),
        send: $t('send'),
        newSearch: $t('imt_no_results.or_new_search'),
      }
    case 'NO_RESULTS':
      return {
        subtitle: $t('imt_no_results.no_results_subtitle'),
        newSearch: $t('imt_no_results.new_search'),
      }
  }
  return {}
})

function getIMTlink() {
  const urlParams = { ...corridor }

  switch (state.value) {
    case 'HIGHER_AMOUNT':
      return getIMTLiveUrl({ ...urlParams, amount: newAmount.value })
    case 'TO_DEFAULT':
    case 'TO_USD':
      if (suggestedCurrency.value) {
        urlParams.currencyTo = suggestedCurrency.value
      }
      break
    case 'FROM_USD':
      if (suggestedCurrency.value) {
        urlParams.currencyFrom = suggestedCurrency.value
        urlParams.amount = newAmount.value
      }
      break
    default:
      break
  }

  const redirectionParam = corridor.payout ? `&noResultsRedirect=true` : `?noResultsRedirect=true`
  return `${getIMTLiveUrl(urlParams)}${redirectionParam}`
}

async function onSubmit() {
  await navigate(getIMTlink())
}

onMounted(async () => {
  await saveComparisonInfo()
  const mapState = {
    HIGHER_AMOUNT: 'higher_amount',
    TO_DEFAULT: 'default_currency_to',
    TO_USD: 'usd_currency_to',
    FROM_USD: 'usd_currency_from',
    NO_RESULTS: 'no_results',
  }
  await noResultsWarning($track, corridor, props.amountUsd, mapState[state.value])
})

async function saveComparisonInfo() {
  await storeLastComparisonInfo({
    countryFrom: corridor.countryFrom.iso,
    countryTo: corridor.countryTo.iso,
    currencyFrom: corridor.currencyFrom.iso,
    currencyTo: corridor.currencyTo.iso,
    amount: corridor.amount || corridor.defaultAmount,
    payout: corridor.payout,
    cheapestProvider: null,
    spotlightedProvider: null,
    liveComparison: !props.landingMode,
    results: null,
    midMarketRate: 0,
    timestamp: Date.now(),
  })
}

function onInvalid(event: Event) {
  const target = event.target as HTMLInputElement
  target.setCustomValidity($t('transfer_search.amount_is_required'))
}

function onInput(event: Event) {
  const target = event.target as HTMLInputElement
  target.setCustomValidity('')

  if (Number(target.value) > subscriptions.maxAmount) {
    target.value = subscriptions.maxAmount.toString()
  }
}
</script>
<style scoped>
.arrow::after {
  content: '';
  @apply absolute -bottom-2 left-5;
  border-color: theme('colors.brand.800') transparent transparent;
  border-width: theme('spacing.4') theme('spacing.4') 0;
}
</style>
